// KMZ/KML → Shapefile (Web Mercator) converter — runs entirely in the browser.
// Reuses the exact shapefile-writing approach proven in ViewMapModal: the low-level
// @mapbox/shp-write `write` (one record per feature) + a byte-exact Web Mercator .prj.

const WEB_MERCATOR_PRJ = 'PROJCS["WGS_1984_Web_Mercator_Auxiliary_Sphere",GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.017453292519943295]],PROJECTION["Mercator_Auxiliary_Sphere"],PARAMETER["False_Easting",0.0],PARAMETER["False_Northing",0.0],PARAMETER["Central_Meridian",0.0],PARAMETER["Standard_Parallel_1",0.0],PARAMETER["Auxiliary_Sphere_Type",0.0],UNIT["Meter",1.0]]';

function sanitizeName(name) {
  return (String(name).replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 60)) || 'layer';
}

function shpTypeOf(t) {
  if (t === 'Point') return 'POINT';
  if (t === 'LineString') return 'POLYLINE';
  if (t === 'Polygon') return 'POLYGON';
  return null;
}

// Shape one feature's coordinates so the low-level writer emits exactly ONE record.
function geomElement(geom) {
  if (geom.type === 'LineString') return [geom.coordinates];
  return geom.coordinates;
}

// DBF field names: ≤10 chars, unique.
function buildFieldMap(keys) {
  const map = {};
  const used = new Set();
  for (const k of keys) {
    const base = (String(k).replace(/[^A-Za-z0-9_]/g, '_').slice(0, 10)) || 'FIELD';
    let name = base;
    let n = 1;
    while (used.has(name.toLowerCase())) {
      const suffix = String(n);
      name = base.slice(0, 10 - suffix.length) + suffix;
      n += 1;
    }
    used.add(name.toLowerCase());
    map[k] = name;
  }
  return map;
}

// WGS84 lon/lat (KML's native CRS) → Web Mercator (EPSG:3857/102100) meters.
const R = 20037508.34;
function toWebMercator([lon, lat]) {
  const clampedLat = Math.max(Math.min(lat, 89.9), -89.9);
  const x = (lon * R) / 180;
  const y = (Math.log(Math.tan(((90 + clampedLat) * Math.PI) / 360)) / (Math.PI / 180)) * (R / 180);
  return [x, y];
}

function stripHtml(s) {
  return String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function textOf(node, tag) {
  const el = node.getElementsByTagName(tag)[0];
  return el ? el.textContent.trim() : '';
}

function parseCoords(text) {
  return String(text).trim().split(/\s+/).filter(Boolean).map((tok) => {
    const parts = tok.split(',');
    return [Number(parts[0]), Number(parts[1])];
  }).filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
}

function closeRing(ring) {
  if (ring.length < 3) return ring;
  const a = ring[0];
  const b = ring[ring.length - 1];
  if (a[0] !== b[0] || a[1] !== b[1]) return [...ring, a];
  return ring;
}

// Parse KML text → array of features { geometry (Web Mercator), properties }.
function parseKML(kmlString) {
  const dom = new DOMParser().parseFromString(kmlString, 'application/xml');
  if (dom.getElementsByTagName('parsererror').length) throw new Error('The KML/KMZ file could not be parsed.');

  const features = [];
  const placemarks = Array.from(dom.getElementsByTagName('Placemark'));
  for (const pm of placemarks) {
    const props = {
      name: textOf(pm, 'name'),
      description: stripHtml(textOf(pm, 'description')).slice(0, 254),
    };

    for (const pt of Array.from(pm.getElementsByTagName('Point'))) {
      const c = parseCoords(textOf(pt, 'coordinates'))[0];
      if (c) features.push({ geometry: { type: 'Point', coordinates: toWebMercator(c) }, properties: { ...props } });
    }
    for (const ls of Array.from(pm.getElementsByTagName('LineString'))) {
      const cs = parseCoords(textOf(ls, 'coordinates'));
      if (cs.length > 1) features.push({ geometry: { type: 'LineString', coordinates: cs.map(toWebMercator) }, properties: { ...props } });
    }
    for (const pg of Array.from(pm.getElementsByTagName('Polygon'))) {
      const rings = [];
      const outer = pg.getElementsByTagName('outerBoundaryIs')[0];
      if (outer) {
        const cs = parseCoords(textOf(outer, 'coordinates'));
        if (cs.length >= 3) rings.push(closeRing(cs.map(toWebMercator)));
      }
      for (const inner of Array.from(pg.getElementsByTagName('innerBoundaryIs'))) {
        const cs = parseCoords(textOf(inner, 'coordinates'));
        if (cs.length >= 3) rings.push(closeRing(cs.map(toWebMercator)));
      }
      if (rings.length) features.push({ geometry: { type: 'Polygon', coordinates: rings }, properties: { ...props } });
    }
  }
  return features;
}

// Read the KML text out of a File (handles both raw .kml and zipped .kmz).
async function readKmlText(file, JSZip) {
  const isKmz = /\.kmz$/i.test(file.name);
  if (!isKmz) return await file.text();
  const zip = await JSZip.loadAsync(file);
  const names = Object.keys(zip.files);
  const kmlName = names.find((n) => /doc\.kml$/i.test(n)) || names.find((n) => /\.kml$/i.test(n));
  if (!kmlName) throw new Error('No .kml found inside the KMZ.');
  return await zip.files[kmlName].async('string');
}

// Main entry: File → { blob, filename, summary } ready to download.
export async function convertKmlToShp(file) {
  const [smod, jszipMod] = await Promise.all([import('@mapbox/shp-write'), import('jszip')]);
  const writeShp = smod.write || smod.default?.write;
  const JSZip = jszipMod.default || jszipMod;

  const kmlText = await readKmlText(file, JSZip);
  const features = parseKML(kmlText);
  if (!features.length) throw new Error('No points, lines, or polygons found in this file.');

  const buildShp = (shpType, feats) => {
    const keys = [...new Set(feats.flatMap((f) => Object.keys(f.properties || {})))];
    const fieldMap = buildFieldMap(keys);
    const rows = feats.map((f) => {
      const out = {};
      for (const k of Object.keys(f.properties || {})) out[fieldMap[k]] = f.properties[k];
      return out;
    });
    const geometries = feats.map((f) => geomElement(f.geometry));
    let out = null;
    writeShp(rows, shpType, geometries, (err, files) => { if (!err) out = files; });
    return out;
  };

  const base = sanitizeName(file.name.replace(/\.(kmz|kml)$/i, ''));
  const groups = { POINT: [], POLYLINE: [], POLYGON: [] };
  for (const f of features) {
    const t = shpTypeOf(f.geometry?.type);
    if (t) groups[t].push(f);
  }
  const present = Object.keys(groups).filter((t) => groups[t].length);

  const suffix = { POINT: 'points', POLYLINE: 'lines', POLYGON: 'polygons' };
  const summary = { POINT: groups.POINT.length, POLYLINE: groups.POLYLINE.length, POLYGON: groups.POLYGON.length };

  // One flat zip containing every shapefile (distinct basenames per geometry type).
  // ArcGIS Online reads a multi-shapefile zip as a single hosted feature layer with sublayers.
  const master = new JSZip();
  let built = 0;
  for (const t of present) {
    const files = buildShp(t, groups[t]);
    if (!files) continue;
    const name = present.length > 1 ? `${base}_${suffix[t]}` : base;
    master.file(`${name}.shp`, files.shp.buffer, { binary: true });
    master.file(`${name}.shx`, files.shx.buffer, { binary: true });
    master.file(`${name}.dbf`, files.dbf.buffer, { binary: true });
    master.file(`${name}.prj`, WEB_MERCATOR_PRJ);
    built += 1;
  }
  if (!built) throw new Error('Failed to build a shapefile from this file.');

  const blob = await master.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  return { blob, filename: `${base}_shp.zip`, summary, multi: built > 1 };
}
